import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackSessionTimeout } from 'utils/analytics';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Detects user inactivity and fires `session_timeout` with the last known screen.
 * Resets on any user interaction (mouse, touch, keyboard) or route change.
 * Place this hook inside a component that is inside <Router>.
 */
const useSessionTimeout = (): void => {
  const location = useLocation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScreenRef = useRef<string>(location.pathname);

  useEffect(() => {
    lastScreenRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        trackSessionTimeout(lastScreenRef.current);
      }, INACTIVITY_TIMEOUT_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));

    // Start the timer immediately
    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, []);
};

export default useSessionTimeout;
